import React, { useState } from 'react';
import {
	PiArrowsDownUpThin,
	PiArrowUpThin,
	PiArrowDownThin,
} from 'react-icons/pi';
import { DocumentSearchIcon } from '@svgs';
import { TableProps, CellRendererProps, SortIconProps } from '@interface';
import { formatCurrency } from '@utils';
import { propertyRoutes } from '@services';

const DOCUMENT_URL = process.env.REACT_APP_PROPERTY_DOCUMENT_BASE_URL;
const BISWEB_URL = process.env.REACT_APP_BISWEB_BASE_URL;


interface TableCellRendererProps extends CellRendererProps {
	toggleExpand: () => void;
	isExpanded: boolean;
	selectTab: string;
}

const LinkCell: React.FC<{
	href: string;
	iconHref?: string;
	value: string | number;
	icon?: boolean;
}> = ({ iconHref='', href, value, icon = false }) => (
	<div className="flex items-center space-x-2">
		<a
			href={href}
			className="text-link"
			target="_blank"
			rel="noopener noreferrer"
		>
			{value}
		</a>
		{(icon && iconHref) && (
			<a
				className="inline-block bg-gray-300 rounded-full w-5 h-5 text-center"
				href={iconHref}
				target="_blank"
				rel="noopener noreferrer"
			>
				<img src={DocumentSearchIcon} alt="Document Icon" />
			</a>
		)}
	</div>
);

const TableCellRenderer: React.FC<TableCellRendererProps> = ({
	value,
	column,
	selectTab,
	toggleExpand,
	isExpanded,
}) => {
	const columnMapping: Record<string, Record<string, string>> = {
		salesAndFinancing: {
			'Document ID': `${DOCUMENT_URL}${propertyRoutes.documentDetailView}?doc_id=${value}`,
			'Document Image': `${DOCUMENT_URL}${propertyRoutes.documentImageView}?doc_id=${value}`,
		},
		dobComplaints: {
			'Complaint #': `${BISWEB_URL}${propertyRoutes.OverviewForComplaintServlet}?complaintno=${value}&&go6=+GO+&requestid=0`,
		},
		ecbViolations: {
			'Violation #': `${BISWEB_URL}${propertyRoutes.ecbQueryByNumberServlet}?ecbin=${value}&go7=+GO+&requestid=0`,
		},
		dobFiledPermits: {
			'Job Filing #': `${BISWEB_URL}${propertyRoutes.jobsQueryByNumberServlet}?passjobnumber=${value}&passdocnumber=&go10=+GO+&requestid=0`,
		},
		dobIssuedPermits: {
			'Job #': `${BISWEB_URL}${propertyRoutes.jobsQueryByNumberServlet}?passjobnumber=${value}&passdocnumber=&go10=+GO+&requestid=0`,
		},
	};

	if (selectTab === 'salesAndFinancing') {
		if (column === 'Document ID') {
			return (
				<LinkCell
					iconHref = {columnMapping[selectTab]['Document Image']}
					href={columnMapping[selectTab][column]}
					value={value}
					icon
				/>
			);
		} else if (column === 'Amount') {
			return <span>{formatCurrency(value)}</span>;
		} else if (['Document Date', 'Filing Date'].includes(column)) {
			return <span>{value}</span>;
		}
	}

	if (columnMapping[selectTab]?.[column]) {
		return <LinkCell href={columnMapping[selectTab][column]} value={value} />;
	}

	if (column === 'Latest sale price') {
		value = (value === 0 || value ) &&`$${value}`;
	}
		return (
			<span
				className={`truncate cursor-pointer ${isExpanded ? 'text-blue-500 font-semibold' : ''}`}
				onClick={toggleExpand}
			>
				{value}
			</span>
		);
};

const SortIcon: React.FC<SortIconProps> = ({ column, sortConfig }) => {
	const iconClass = 'h-6 w-6';
	if (sortConfig.key !== column) return <PiArrowsDownUpThin className={iconClass} />;
	return sortConfig.direction === 'asc' ? (
		<PiArrowUpThin className={iconClass} />
	) : (
		<PiArrowDownThin className={iconClass} />
	);
};

export const TableBody: React.FC<TableProps> = ({
	sortConfig,
	columns,
	requestSort,
	paginatedData,
	selectTab = '',
}) => {
	const [expandedCell, setExpandedCell] = useState<{
		rowIndex: number;
		colKey: string;
	} | null>(null);

	const toggleExpand = (rowIndex: number, colKey: string) => {
		setExpandedCell((prev) =>
			prev?.rowIndex === rowIndex && prev?.colKey === colKey
				? null
				: { rowIndex, colKey }
		);
	};

	return (
		<div className="overflow-x-auto">
			<table className="min-w-full bg-white border text-[14px]">
				<thead>
					<tr>
						{columns.map(({ key, label }) => (
							<th
								key={key}
								onClick={() => requestSort(key)}
								className="border border-gray-300 cursor-pointer"
							>
								<div className="inline-flex items-center">
									<div>
									{label}
									</div>
									<div>
									<SortIcon column={key} sortConfig={sortConfig} />
									</div>
								</div>
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{paginatedData.map((row, rowIndex) => (
						<React.Fragment key={rowIndex}>
							<tr className="hover:bg-gray-50">
								{columns.map(({ key }) => (
									<td
										key={key}
										className="p-[5px] border border-gray-300 truncate max-w-60 whitespace-nowrap cursor-pointer hover:bg-gray-100"
									>
										<TableCellRenderer
											value={row[key]}
											column={key}
											selectTab={selectTab}
											row={row}
											isExpanded={
												expandedCell?.rowIndex === rowIndex &&
												expandedCell?.colKey === key
											}
											toggleExpand={() => toggleExpand(rowIndex, key)}
										/>
									</td>
								))}
							</tr>
							{/* Expanded Row */}
							{expandedCell?.rowIndex === rowIndex && (
								<tr className="bg-gray-100">
									<td
										colSpan={columns.length}
										className="p-4 border border-gray-300"
									>
										<strong>
											{
												columns.find((col) => col.key === expandedCell?.colKey)
													?.label
											}
											:
										</strong>{' '}
										{row[expandedCell.colKey]}
									</td>
								</tr>
							)}
						</React.Fragment>
					))}
				</tbody>
			</table>
		</div>
	);
};
